import type { Community, Translations } from '../types';
import { events } from '../lib/firebase';

interface CommunityCardsProps {
  t: Translations;
  communities: Community[];
  onCommunityClick: (community: Community) => void;
}

export function CommunityCards({ t, communities, onCommunityClick }: CommunityCardsProps) {
  const handleClick = (community: Community) => {
    events.communityCardClick(community.id, community.status);
    onCommunityClick(community);
  };

  return (
    <section className="bg-gray-50 py-12 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <h2 className="mb-4">{t.selectCommunity}</h2>
          <p className="text-gray-600">{t.selectCommunityDesc}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {communities.map((community) => (
            <button
              key={community.id}
              onClick={() => handleClick(community)}
              className="px-4 py-3 bg-white rounded-lg border border-gray-200 transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-vinotinto text-left flex items-center gap-3"
            >
              <span className="text-3xl">{community.flag}</span>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <h3 className="font-semibold truncate">
                  {community.id === 'global' ? t.everywhereElse : community.name}
                </h3>
                {community.status === 'active' ? (
                  <ActiveBadge label={t.active} />
                ) : (
                  <IncubatingBadge label={t.incubating} />
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Sprint 81 BUG-81-08 §5: "Explorar Más Comunidades" button removed —
            no content behind it, surfacing dead-end UX. The translation
            string `t.exploreMore` is retained for backwards-compat. */}
      </div>
    </section>
  );
}

function ActiveBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-50">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-pulse-dot absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
      </span>
      <span className="text-[10px] font-medium text-green-600">{label}</span>
    </span>
  );
}

function IncubatingBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-50">
      <span className="animate-pulse-egg text-[10px]">🥚</span>
      <span className="text-[10px] font-medium text-orange-600">{label}</span>
    </span>
  );
}
